import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IconButton, Tooltip } from '@material-ui/core';
import AnnotatedVideo from '../AnnotatedVideo/AnnotatedVideo';
import Skip from '../Skip/Skip';
import SkipIcon from '@material-ui/icons/DoubleArrow';
import SetFrameIcon from '@material-ui/icons/ArrowDownward';
import PreviousIcon from '@material-ui/icons/NavigateBefore';
import NextIcon from '@material-ui/icons/NavigateNext';
import Forward5Icon from '@material-ui/icons/Forward5';
import Replay5Icon from '@material-ui/icons/Replay5';

interface Props {
  apikey: string;
  framerate?: number;
  baseUrl?: string;
  videos: Array<TSync>;
  onUpdate: (sync: Array<TSync> | null) => void;
}

export type TVideo = {
  url: string;
};

export type TSync = {
  name?: string;
  url: string;
  offset: number;
};

const syncOutput = (frames: { [key: string]: number }, videos: Array<TVideo>, framerate: number = 25) => {
  //Find largest frame number and base every offset on that
  let largest = 0;
  for (let video of videos) {
    let key = video.url;
    if (frames[key] > largest) largest = frames[key];
  }
  return videos.map((video) => {
    return { url: video.url, offset: Math.round((largest - frames[video.url]) * (1000 / framerate)) };
  });
};

const SyncView: React.FunctionComponent<Props> = ({
  apikey,
  baseUrl,
  videos: unsortedVideos,
  onUpdate,
  framerate,
}: Props) => {
  const [selectedView, setSelectedView] = useState(0);
  const [bigVideos, setBigVideos] = useState([0, 1]);
  const [locked, setLocked] = useState<{ [key: string]: boolean }>({});
  const [frames, setFrames] = useState<{ [key: string]: number }>({});
  const [skip, setSkip] = useState(false);
  const [goto, setGoto] = useState(false);

  const videos = useMemo(() => {
    let ret = unsortedVideos.map((v, i) => (v.name ? v : { ...v, name: 'Video ' + i }));
    return ret.sort((a, b) => (a.name && b.name ? a.name?.localeCompare(b.name) : -1));
  }, [unsortedVideos]);

  useEffect(() => {
    const update = {};
    let largest = 0;
    for (const video of videos) {
      if (video.offset > largest) {
        largest = video.offset;
      }
    }
    for (const video of videos) {
      if (!frames[video.url]) {
        update[video.url] = 1 + (largest - video.offset || 0) / (1000 / (framerate || 25));
      }
    }
    if (Object.keys(update).length > 0) {
      setFrames(update);
    }
  }, [videos, frames, framerate]);

  useEffect(() => {
    const update = {};
    videos.forEach((video) => {
      update[video.url] = true;
    });
    setLocked(update);
  }, [videos]);

  const setFrame = useCallback(
    (index, frame) => {
      setFrames((f) => {
        const newval = Object.assign({}, f);
        const url = videos[index].url;
        newval[url] = frame;
        if (locked[url]) {
          const diff = frame - f[url];
          for (let lockIndex in locked) {
            if (locked[lockIndex] && lockIndex !== url && videos.find((v) => v.url === lockIndex)) {
              if (newval[lockIndex]) newval[lockIndex] += diff;
              else newval[lockIndex] = diff;
            }
          }
        }
        for (let key of Object.keys(newval)) {
          if (newval[key] < 1) return f; //return previous value because we don not allow any of the frame indexes to be smaller than 1
        }
        return newval;
      });
    },
    [locked, videos, setFrames]
  );

  useEffect(() => {
    const keydown = (e: any) => {
      let frame;
      if (frames && videos && videos[bigVideos[selectedView]]) frame = frames[videos[bigVideos[selectedView]].url];
      if (e.code === 'KeyF' && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setSkip(true);
      } else if (e.code === 'KeyG' && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setGoto(true);
      } else if (e.code === 'KeyP' && frame && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setFrame(bigVideos[selectedView], frame - 1);
      } else if (e.code === 'KeyN' && frame && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setFrame(bigVideos[selectedView], frame + 1);
      } else if (e.code === 'Digit1' && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedView(0);
      } else if (e.code === 'Digit2' && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedView(1);
      } else if (e.keyCode === 37 && frame && frame > 5 && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setFrame(bigVideos[selectedView], frame - 5);
      } else if (e.keyCode === 39 && frame && !skip && !goto) {
        e.preventDefault();
        e.stopPropagation();
        setFrame(bigVideos[selectedView], frame + 5);
      }
    };

    document.addEventListener('keydown', keydown);
    return () => document.removeEventListener('keydown', keydown);
  }, [videos, frames, setFrame, bigVideos, goto, selectedView, skip]);

  useEffect(() => {
    let numLocked = 0;
    for (let key of videos) {
      if (locked[key.url] === true) {
        numLocked++;
      }
    }
    if (numLocked > 0 && numLocked === videos.length) {
      onUpdate(syncOutput(frames, videos, framerate));
    } else {
      onUpdate(null);
    }
  }, [locked, videos, frames, onUpdate, framerate]);

  const onToggleLock = useCallback(
    (index: number) => {
      setLocked((l) => {
        return { ...l, [videos[index].url]: !l[videos[index].url] };
      });
    },
    [videos]
  );

  const mapvideo = (row: number, numberOfVideos: number) => (video: TSync, index: number) => {
    const i = Math.ceil(index + (row * numberOfVideos) / 2);
    const temp = bigVideos.slice(0);
    temp[selectedView] = i;
    return (
      <SmallVideo
        numberOfVideos={numberOfVideos}
        selected={bigVideos[selectedView] === i}
        onClick={() => {
          setBigVideos(temp);
        }}
        key={i}>
        <AnnotatedVideo
          baseUrl={baseUrl}
          apikey={apikey}
          video={video}
          i={i}
          locked={locked[videos[i].url]}
          onToggleLock={onToggleLock}
          frame={frames[videos[i].url]}
          setFrame={setFrame}
          interactive={false}
          framerate={framerate}
        />
      </SmallVideo>
    );
  };

  const onSkip = useCallback(
    (skipFrames: number) => {
      let valid = true;
      for (let key in frames) {
        if (frames[key] + skipFrames < 1 && videos.find((v) => v.url === key)) {
          valid = false;
          break;
        }
      }
      if (valid) {
        setFrames((f) => {
          const newval = Object.assign({}, f);
          for (let key in newval) {
            if (videos.find((v) => v.url === key)) {
              newval[key] = newval[key] + skipFrames;
              if (newval[key] < 1) newval[key] = 1;
            }
          }
          return newval;
        });
      }
    },
    [frames, videos]
  );

  const onGoto = useCallback(
    (frame: number) => {
      setFrame(selectedView, frame);
    },
    [selectedView, setFrame]
  );

  return (
    <Wrapper>
      <Skip key="skip" open={skip} onClose={() => setSkip(false)} onSkip={onSkip} />
      <Skip
        key="goto"
        open={goto}
        onClose={() => setGoto(false)}
        onSkip={onGoto}
        title="Goto frame"
        text="Enter the frame number to go to"
      />
      <span>
        <Tooltip title='Set Frame [G-key]' aria-label="set">
          <IconButton color="primary" aria-label="set" onClick={() => setGoto(true)}>
            <SetFrameIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Skip Frames [F-key]' aria-label="skip">
          <IconButton color="primary" aria-label="skip" onClick={() => setSkip(true)}>
            <SkipIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Back 5 Frames [<-key]' aria-label="prev5">
          <IconButton color="primary" aria-label="prev5" onClick={() => setFrame(bigVideos[selectedView], frames[videos[bigVideos[selectedView]].url] - 5)}>
            <Replay5Icon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Previous Frame [P-key]' aria-label="prev">
          <IconButton color="primary" aria-label="prev" onClick={() => setFrame(bigVideos[selectedView], frames[videos[bigVideos[selectedView]].url] - 1)}>
            <PreviousIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Next Frame [N-key]' aria-label="next">
          <IconButton color="primary" aria-label="next" onClick={() => setFrame(bigVideos[selectedView], frames[videos[bigVideos[selectedView]].url] + 1)}>
            <NextIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Skip 5 Frames [>-key]' aria-label="next5">
          <IconButton color="primary" aria-label="next5" onClick={() => setFrame(bigVideos[selectedView], frames[videos[bigVideos[selectedView]].url] + 5)}>
            <Forward5Icon />
          </IconButton>
        </Tooltip>
      </span>
      <TopWrapper>
        <BigVideo key={0} selected={selectedView === 0} onClick={() => setSelectedView(0)}>
          {videos.length > 0 && (
            <AnnotatedVideo
              key={0}
              baseUrl={baseUrl}
              apikey={apikey}
              video={videos[bigVideos[0]]}
              i={bigVideos[0]}
              locked={locked[videos[bigVideos[0]].url]}
              onToggleLock={onToggleLock}
              frame={frames[videos[bigVideos[0]].url]}
              setFrame={setFrame}
              interactive
              framerate={framerate}
            />
          )}
        </BigVideo>
        <BigVideo key={1} selected={selectedView === 1} onClick={() => setSelectedView(1)}>
          {videos.length > 1 && (
            <AnnotatedVideo
              key={1}
              baseUrl={baseUrl}
              apikey={apikey}
              video={videos[bigVideos[1]]}
              i={bigVideos[1]}
              locked={locked[videos[bigVideos[1]].url]}
              onToggleLock={onToggleLock}
              frame={frames[videos[bigVideos[1]].url] || 1}
              setFrame={setFrame}
              interactive
              framerate={framerate}
            />
          )}
        </BigVideo>
      </TopWrapper>
      <BottomWrapper>
        <BottomRowWrapper first key="top">
          {videos.filter((_video, index) => index < videos.length / 2).map(mapvideo(0, videos.length))}
        </BottomRowWrapper>
        <BottomRowWrapper key="bottom">
          {videos.filter((_video, index) => index >= videos.length / 2).map(mapvideo(1, videos.length))}
        </BottomRowWrapper>
      </BottomWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const TopWrapper = styled.div`
  background-color: black;
  display: flex;
  flex-direction: row;
  height: 70%;
  align-items: flex-start;
`;

type BigVideoProps = {
  selected: boolean;
};

const BigVideo = styled.div<BigVideoProps>`
  border: ${(props) => (props.selected ? '1px solid blue' : '1px solid darkgrey')};
  background-color: black;
  width: 50%;
  height: 100%;
  display: flex;
  padding: 5px;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 30%;
  background-color: black;
`;

type WrapperProps = {
  first?: boolean;
};

const BottomRowWrapper = styled.div<WrapperProps>`
  display: flex;
  height: 50%;
  flex-direction: row;
  align-items: ${(props) => (props.first ? 'flex-end' : 'flex-start')};
`;

type SmallVideoProps = {
  numberOfVideos: number;
  selected: boolean;
};

const SmallVideo = styled.div<SmallVideoProps>`
  height: 100%;
  background-color: black;
  padding: 5px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: ${(props) => (props.selected ? '3px solid darkgrey' : '1px solid darkgrey')};
  width: ${(props) => 200 / props.numberOfVideos + '%'};
`;

(SyncView as any).whyDidYouRender = true;
export default SyncView;
